import { useState, useEffect } from 'react';
import { Package, Plus, Edit, Trash2, ShoppingCart, DollarSign, Clock, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Bundle {
  id: number;
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  is_active: boolean;
  created_at: string;
  product_count?: number;
  item_count?: number;
  purchase_count?: number;
  total_revenue?: number;
  stock_limit?: number | null;
  stock_sold?: number;
  starts_at?: string | null;
  ends_at?: string | null;
}

export default function UpsellBundles() {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchBundles();
  }, []);

  async function fetchBundles() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ bundles: Bundle[] }>('/admin/bundles');
      setBundles(response.bundles || []);
    } catch (err) {
      setError('Failed to load bundles. Please try again.');
      console.error('Error fetching bundles:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleToggleActive(id: number, currentState: boolean) {
    try {
      setError(null);
      await api.patch(`/admin/bundles/${id}`, {
        is_active: !currentState,
      });
      setSuccessMessage(`Bundle ${!currentState ? 'activated' : 'deactivated'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setBundles(prev => prev.map(b => b.id === id ? { ...b, is_active: !currentState } : b));
    } catch (err) {
      setError('Failed to update bundle. Please try again.');
      console.error('Error updating bundle:', err);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Are you sure you want to delete this bundle?')) {
      return;
    }

    try {
      setError(null);
      await api.delete(`/admin/bundles/${id}`);
      setSuccessMessage('Bundle deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setBundles(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      setError('Failed to delete bundle. Please try again.');
      console.error('Error deleting bundle:', err);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading bundles...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-8 h-8 text-gray-700" aria-hidden="true" />
              <h1 className="text-3xl font-bold text-gray-900">Product Bundles</h1>
            </div>
            <p className="text-gray-600">Create curated product bundles with discounted pricing</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/upsell/bundles/analytics">
              <Button type="button" variant="outline" size="md">
                <BarChart3 className="w-4 h-4 mr-2" aria-hidden="true" />
                Analytics
              </Button>
            </Link>
            <Link to="/admin/upsell/bundles/new">
              <Button type="button" variant="primary" size="md">
                <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                Create Bundle
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {bundles.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-stone-300 mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-stone-900 mb-2">No bundles yet</h3>
            <p className="text-stone-600 mb-6">
              Create your first product bundle to increase average order value
            </p>
            <Link to="/admin/upsell/bundles/new">
              <Button type="button" variant="primary" size="md">
                <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                Create Your First Bundle
              </Button>
            </Link>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {bundles.map(bundle => (
            <Card key={bundle.id} padding="lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-stone-900">{bundle.name}</h3>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        bundle.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-stone-100 text-stone-600'
                      }`}
                    >
                      {bundle.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-stone-600 mb-3">{bundle.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                    <span>
                      Discount:{' '}
                      {bundle.discount_type === 'percentage'
                        ? `${bundle.discount_value}%`
                        : formatCurrency(bundle.discount_value)}
                    </span>
                    <span>{bundle.item_count || bundle.product_count || 0} products</span>
                    {Number(bundle.purchase_count) > 0 && (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <ShoppingCart className="h-3.5 w-3.5" aria-hidden="true" />
                        {bundle.purchase_count} purchased
                      </span>
                    )}
                    {Number(bundle.total_revenue) > 0 && (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <DollarSign className="h-3.5 w-3.5" aria-hidden="true" />
                        {formatCurrency(Number(bundle.total_revenue))} saved
                      </span>
                    )}
                    {bundle.stock_limit != null && (
                      <span>{bundle.stock_sold || 0}/{bundle.stock_limit} sold</span>
                    )}
                    {bundle.ends_at && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                        Ends {new Date(bundle.ends_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Link to={`/admin/upsell/bundles/${bundle.id}/edit`}>
                    <Button type="button" variant="outline" size="sm">
                      <Edit className="w-4 h-4 mr-1" aria-hidden="true" />
                      Edit
                    </Button>
                  </Link>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(bundle.id, bundle.is_active)}
                  >
                    {bundle.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(bundle.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
